import { Button } from '@edsn/pie-components';
import { useQueryClient } from '@tanstack/react-query';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCurrentOrganisation } from '#pie/auth';
import {
  type ContactGroupFormFieldValues,
  ContactGroupsFormFields,
} from '#pie/components/contact-modal/ContactGroupsFormFields';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import { Text } from '#pie/components/text/Text';
import {
  type CprContactGroupMembership,
  getGetManagementContactQueryKey,
  useCreateContactGroupMembership,
  useGetContactGroups,
} from '#pie/internal/api/pie-bff';
import { Stack } from '../stack/Stack';
import { useToast } from '../toast/ToastContext';

interface AddContactGroupModalProps {
  open: boolean;
  contactPersonId: string;
  onOpenChange: (open: boolean) => void;
  activeMemberships: CprContactGroupMembership[];
}

export const AddContactGroupModal = ({
  open,
  contactPersonId,
  onOpenChange,
  activeMemberships,
}: AddContactGroupModalProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { currentAccount } = useCurrentOrganisation();
  const { addToast } = useToast();

  const formMethods = useForm<ContactGroupFormFieldValues>({
    defaultValues: { contactGroups: [] },
  });
  const {
    formState: { isSubmitting },
    handleSubmit,
    reset,
    watch,
  } = formMethods;
  const marketRole = currentAccount?.marketRole;
  const { data: allContactGroups, isLoading: isLoadingGroups } =
    useGetContactGroups();

  const availableContactGroups = (allContactGroups ?? []).filter(
    ({ marketRoles }) => !marketRole || marketRoles.includes(marketRole),
  );

  const subOrganisations = currentAccount?.subOrganisations ?? [];
  const selectedOrganisationId = watch('organisationId');
  const targetOrganisationId =
    selectedOrganisationId ??
    (subOrganisations.length === 1 ? subOrganisations[0].id : undefined);

  const contactGroupIdsForSubunit = (subunitId: string) =>
    activeMemberships
      .filter(({ subunit }) => subunit.id === subunitId)
      .map(({ contactGroup }) => contactGroup.id);

  const hasAvailableGroupsForSubunit = (subunitId: string) =>
    availableContactGroups.some(
      ({ id }) => !contactGroupIdsForSubunit(subunitId).includes(id),
    );

  const excludeContactGroupIds = targetOrganisationId
    ? contactGroupIdsForSubunit(targetOrganisationId)
    : [];

  const availableOrganisationIds = subOrganisations
    .filter(({ id }) => hasAvailableGroupsForSubunit(id))
    .map(({ id }) => id);

  const hasAvailableGroups =
    isLoadingGroups ||
    (targetOrganisationId
      ? hasAvailableGroupsForSubunit(targetOrganisationId)
      : availableOrganisationIds.length > 0);

  const { mutateAsync, isPending } = useCreateContactGroupMembership();

  const onSubmit: SubmitHandler<ContactGroupFormFieldValues> = async ({
    contactGroups,
    organisationId,
  }) => {
    if (!contactPersonId || !contactGroups?.length) return;

    try {
      await Promise.all(
        contactGroups.map((contactGroupId) =>
          mutateAsync({
            data: {
              contactId: contactPersonId,
              contactGroupId,
              organisationId: organisationId ?? targetOrganisationId ?? null,
            },
          }),
        ),
      );

      queryClient.invalidateQueries({
        queryKey: getGetManagementContactQueryKey(contactPersonId),
      });

      queryClient.invalidateQueries({
        queryKey: ['/cpr-management-contact/contact'],
      });

      addToast({
        message: t(
          'cpr_management_contact.detail.add_user.toast.success.message',
        ),
        title: t('cpr_management_contact.detail.add_user.toast.success.title'),
        type: 'success',
      });

      reset();
      onOpenChange(false);
    } catch {
      addToast({
        message: t('cpr_contact_detail.add_user.toast.error.message'),
        title: t('cpr_contact_detail.add_user.toast.error.title'),
        type: 'error',
      });
    }
  };

  return (
    <Dialog
      title={t('cpr_management.add_contact_group.title')}
      size="md"
      open={open}
      onOpenChange={onOpenChange}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="md">
            {!hasAvailableGroups ? (
              <Text>
                {t(
                  'cpr_management.add_contact_group.maximum_contactgroups_reached',
                )}
              </Text>
            ) : (
              <ContactGroupsFormFields
                excludeContactGroupIds={excludeContactGroupIds}
                marketRole={marketRole}
                availableOrganisationIds={availableOrganisationIds}
                isLoading={isPending}
              />
            )}
            <div className="flex gap-2">
              <Button
                action="tertiary"
                emphasis="subtle"
                className="w-full"
                type="button"
                onClick={() => onOpenChange(false)}
              >
                {hasAvailableGroups
                  ? t('common.button.cancel')
                  : t('common.close')}
              </Button>
              {hasAvailableGroups && (
                <Button
                  className="w-full"
                  type="submit"
                  isLoading={isPending}
                  disabled={isPending || isSubmitting}
                >
                  {t('cpr_management_group_detail.button.add')}
                </Button>
              )}
            </div>
          </Stack>
        </form>
      </FormProvider>
    </Dialog>
  );
};
